import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
import { getLoaderLocale, translateLoaderCopy } from "./legacy/ui/i18n";
import { loadSettings } from "./legacy/ui/storage";
import { LoginPage } from "./pages/auth/LoginPage";
import { paths } from "./routes/paths";

const ADMIN_ROUTES = [
  "/chat",
  "/overview",
  "/channels",
  "/instances",
  "/sessions",
  "/usage",
  "/cron",
  "/agents",
  "/skills",
  "/nodes",
  "/kb",
  "/config",
  "/debug",
  "/logs",
] as const;

function DashboardSessionBar({ enabled }: { enabled: boolean }) {
  const { logout, profile, roleTitle } = useAuth();
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      setTarget(null);
      return;
    }

    const selector = "[data-assistant-admin-nav-footer]";
    const resolveTarget = () => {
      const nextTarget = document.querySelector<HTMLElement>(selector);
      setTarget((current) => (current === nextTarget ? current : nextTarget));
      return Boolean(nextTarget);
    };

    if (resolveTarget()) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (resolveTarget()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled]);

  if (!profile || !target) {
    return null;
  }

  const name =
    (typeof profile.name === "string" && profile.name) ||
    (typeof profile.full_name === "string" && profile.full_name) ||
    "Tài khoản quản trị";
  const company =
    profile.company && typeof profile.company === "object" && "name" in profile.company
      ? (profile.company as { name?: string }).name
      : null;
  const details = [company, roleTitle].filter((value): value is string => Boolean(value));

  return createPortal(
    <div className="assistant-admin-nav-footer-card">
      <div className="assistant-admin-nav-footer-card__meta">
        <div className="assistant-admin-nav-footer-card__name" title={name}>
          {name}
        </div>
        {details.length ? (
          <div className="assistant-admin-nav-footer-card__details">
            {details.map((item) => (
              <div key={item} className="assistant-admin-nav-footer-card__detail" title={item}>
                {item}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="assistant-admin-nav-footer-card__button"
        onClick={logout}
      >
        Đăng xuất
      </button>
    </div>,
    target,
  );
}

function LegacyDashboardPage() {
  const [ready, setReady] = useState(() => customElements.get("openclaw-app") != null);
  const [loaderLocale] = useState(() => {
    try {
      return loadSettings().locale;
    } catch {
      return getLoaderLocale();
    }
  });
  const loaderCopy = translateLoaderCopy(loaderLocale);

  useEffect(() => {
    document.body.classList.add("assistant-admin-body");
    if (!ready) {
      void import("./legacy/ui/app.ts").then(() => setReady(true));
    }
    return () => {
      document.body.classList.remove("assistant-admin-body");
    };
  }, [ready]);

  return (
    <div className="assistant-admin-shell">
      <div className="assistant-admin-aurora assistant-admin-aurora--one" />
      <div className="assistant-admin-aurora assistant-admin-aurora--two" />
      <div className="assistant-admin-grid" />
      <DashboardSessionBar enabled={ready} />
      {ready ? (
        <openclaw-app />
      ) : (
        <div className="assistant-admin-loader">
          <div className="assistant-admin-loader__badge">{loaderCopy.badge}</div>
          <div className="assistant-admin-loader__title">{loaderCopy.title}</div>
          <div className="assistant-admin-loader__sub">{loaderCopy.subtitle}</div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path={paths.login} element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Navigate to={paths.home} replace />} />
        {ADMIN_ROUTES.map((path) => (
          <Route key={path} path={path} element={<LegacyDashboardPage />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to={paths.home} replace />} />
    </Routes>
  );
}
